/** Unified step submit hook — handles both info and question step submission via the /submit endpoint. */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { streamNdjson } from '@/shared/utils/streamNdjson';
import { sessionKeys } from './useStudySessions';
import { continueKeys } from './useContinue';
import type { GradingPhaseEvent } from '@formency/shared';

// ─── Payload ────────────────────────────────────────────────────────────────

export interface StepSubmitPayload {
  responseJson?: unknown;
  responseType?: string;
  responseTimeMs?: number;
  clientSubmissionId?: string;
  deviceId?: string;
  clientTs?: string;
  confidence?: number;
  readingMs?: number;
  answeringMs?: number;
  revisionCount?: number;
  initialResponseJson?: unknown;
  focusLossCount?: number;
  idleCount?: number;
}

// ─── Response shapes ────────────────────────────────────────────────────────

/** Normal submit response (info or question). */
export interface StepSubmitResponse {
  stepKind: 'info' | 'question';
  response: Record<string, unknown> | null;
  isCorrect: boolean | null;
  score: number | null;
  maxScore: number | null;
  explanation: string | null;
  feedback: string | null;
  correctChoiceIds?: string[];
  blankResults?: Record<string, { isCorrect: boolean; correctAnswer: string }>;
  rankingResults?: {
    optionId: string;
    position: number;
    isCorrect: boolean;
    correctOptionId: string;
  }[];
  classifyResults?: {
    itemId: string;
    chosenCategoryId: string;
    correctCategoryId: string;
    isCorrect: boolean;
  }[];
  hotspotResult?: {
    clickX: number;
    clickY: number;
    clickedRegionId: string | null;
    regions: {
      id: string;
      label: string;
      points: { x: number; y: number }[];
      isCorrect: boolean;
    }[];
  };
  numericResult?: {
    submittedValue: number;
    submittedUnit?: string;
    correctAnswer?: number;
    correctRange?: { min: number; max: number };
    tolerance?: number;
    distance?: number;
    correctUnit?: string;
    unitCorrect?: boolean;
  };
  sessionCompleted: boolean;
  completionData: unknown;
}

/** 409 response when step was already answered. */
export interface StepAlreadyAnsweredResponse {
  alreadyAnswered: true;
  session: unknown; // Full session data for sync
}

export type UnifiedStepResult = StepSubmitResponse | StepAlreadyAnsweredResponse;

// ─── Type guards ────────────────────────────────────────────────────────────

export function isAlreadyAnswered(
  result: UnifiedStepResult
): result is StepAlreadyAnsweredResponse {
  return (
    'alreadyAnswered' in result && (result as StepAlreadyAnsweredResponse).alreadyAnswered === true
  );
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useStepSubmit(options?: { onGradingPhase?: (event: GradingPhaseEvent) => void }) {
  const queryClient = useQueryClient();
  const [gradingPhase, setGradingPhase] = useState<GradingPhaseEvent | null>(null);

  const mutation = useMutation<
    UnifiedStepResult,
    Error,
    {
      sessionId: string;
      position: number;
      payload?: StepSubmitPayload;
      isInfoStep?: boolean;
    }
  >({
    mutationFn: async ({ sessionId, position, payload, isInfoStep }) => {
      const url = `/me/study-sessions/${sessionId}/steps/${position}/submit`;

      if (isInfoStep) {
        // Info steps: simple POST, no streaming
        return api.post<UnifiedStepResult>(url, payload ?? {});
      }

      // Question steps: NDJSON streaming (same pattern as old useSubmitResponse)
      let finalResult: UnifiedStepResult | null = null;

      await streamNdjson<Record<string, unknown>>(
        url,
        { method: 'POST', body: JSON.stringify(payload ?? {}) },
        {
          onEvent: (parsed) => {
            if (parsed.event === 'result') {
              finalResult = parsed.data as UnifiedStepResult;
            } else {
              const phaseEvent = parsed as unknown as GradingPhaseEvent;
              setGradingPhase(phaseEvent);
              options?.onGradingPhase?.(phaseEvent);
            }
          },
          onError: (message) => {
            throw new Error(message || 'Something went wrong');
          },
        }
      );

      if (!finalResult) throw new Error('Stream ended without result');
      return finalResult;
    },
    onSuccess: (result) => {
      setGradingPhase(null);

      if (!isAlreadyAnswered(result) && result.sessionCompleted) {
        // Session was auto-completed server-side — clear caches
        queryClient.setQueryData(sessionKeys.active(), null);
        queryClient.invalidateQueries({ queryKey: ['f', 'enrolments'] });
        queryClient.invalidateQueries({ queryKey: ['f', 'revise'] });
        queryClient.invalidateQueries({ queryKey: ['f', 'stats'] });
        queryClient.invalidateQueries({ queryKey: continueKeys.all });
      }
    },
    onError: () => {
      setGradingPhase(null);
    },
  });

  return { ...mutation, gradingPhase };
}
